import React, { useState, useEffect } from 'react';
import { FiPlus } from "react-icons/fi";
import { format } from "date-fns";
import check from "../../assets/check.svg";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addNotification, getAllNotificationsAdmin, getNotificationById } from '../../ApiController/ApiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Notification = () => {
  const { t } = useTranslation();

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 1,
    totalDocs: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [notificationForm, setNotificationForm] = useState({
    type: '',
    total: 'ALL',
    title: '',
    body: '',
    userId: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchNotifications = async (page) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getAllNotificationsAdmin(page);
      console.log('Actual API Response:', res);

      if (res && Array.isArray(res.docs)) {
        setNotifications(res.docs);
        setPaginationInfo({
          totalPages: res.totalPages,
          totalDocs: res.totalDocs,
          hasNextPage: res.hasNextPage,
          hasPrevPage: res.hasPrevPage,
        });
      } else {
        setError("Received invalid data from the server.");
        toast.error("Could not read notification data.");
        setNotifications([]);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'An error occurred while fetching notifications');
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchNotifications(currentPage);
  }, [currentPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitNotification = async (e) => {
    e.preventDefault();
    try {
      if (!notificationForm.type || !notificationForm.title || !notificationForm.body) {
        toast.error('Please fill in all required fields', { position: 'top-right' });
        return;
      }
      await addNotification(notificationForm);
      toast.success('Notification sent successfully', { position: 'top-right' });
      setShowModal(false);
      setIsSuccessModalOpen(true);
      
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchNotifications(1);
      }
      
      setNotificationForm({
        type: '',
        total: 'ALL',
        title: '',
        body: '',
        userId: ''
      });
    } catch (err) {
      console.error('Error sending notification:', err);
      toast.error(err.message || 'Failed to send notification', { position: 'top-right' });
    }
  };

  const handleViewNotification = async (id) => {
    try {
      const res = await getNotificationById(id);
      if(res.success && res.data) {
        setSelectedNotification(res.data);
        setShowModal2(true);
      } else if (res._id) {
        setSelectedNotification(res);
        setShowModal2(true);
      }
      else {
        toast.error(res.message || 'Could not fetch notification details');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNextPage) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (paginationInfo.hasPrevPage) {
      setCurrentPage(prevPage => prevPage - 1);
    }
  };


  return (
    <div className="text-white relative">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-semibold">{t('notification.title')}</h1>
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-end mb-6 gap-4">
        <button
          onClick={() => setShowModal(true)}
          className="ml-0 md:ml-4 bg-gradient-to-r from-[#6E00FF] to-[#A627FF] hover:opacity-90 px-4 py-2 md:px-5 md:py-2 rounded-xl text-white font-medium flex items-center gap-2 shadow-md w-full md:w-auto justify-center"
        >
          <FiPlus className="text-md" />
          {t('notification.buttons.push')}
        </button>
      </div>

      <div className="overflow-x-auto bg-[#1D2461] rounded-lg">
        <table className="w-full table-auto text-sm text-left">
          <thead className="bg-[#B2BAFF] text-[#202224] font-nunito uppercase text-[12px] md:text-[14px]">
            <tr>
              <th className="p-2 md:p-4">
                <input type="checkbox" className="w-3 h-3 md:w-4 md:h-4" />
              </th>
              <th className="p-2 md:p-4">{t('notification.table.header.templateName')}</th>
              <th className="p-2 md:p-4 hidden md:table-cell">{t('notification.table.header.channel')}</th>
              <th className="p-2 md:p-4">{t('notification.table.header.date')}</th>
              <th className="p-2 md:p-4 hidden md:table-cell">{t('notification.table.header.type')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
                <tr>
                    <td colSpan="6" className="p-4 text-center text-gray-400">Loading...</td>
                </tr>
            ) : notifications.length > 0 ? (
              notifications.map((notification, idx) => (
                <tr key={notification._id || idx} className="border-t border-[#2A2A4D] hover:bg-[#2A306C] cursor-pointer" onClick={() => handleViewNotification(notification._id)}>
                  <td className="p-2 md:p-4">
                    <input type="checkbox" className="w-3 h-3 md:w-4 md:h-4" onClick={(e) => e.stopPropagation()} />
                  </td>
                  {/* === THIS IS THE ONLY CHANGED LINE === */}
                  <td className="p-2 md:p-4">
                    {notification.title || (notification.body ? `${notification.body.substring(0, 50)}...` : 'N/A')}
                  </td>
                  <td className="p-2 md:p-4 hidden md:table-cell">
                    {notification.userId && notification.userId._id
                      ? t('notification.table.body.specificUser')
                      : t('notification.table.body.allChannels')}
                  </td>
                  <td className="p-2 md:p-4">
                    {notification.createdAt
                      ? format(new Date(notification.createdAt), isMobile ? 'MM/dd/yyyy' : 'PP')
                      : 'N/A'}
                  </td>
                  <td className="p-2 md:p-4 hidden md:table-cell">{notification.type || 'N/A'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-400">
                  {t('notification.table.body.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <div className="flex items-center justify-between text-sm text-gray-400 p-2 md:p-4 border-t border-[#2A2A4D]">
            <div>
                {t('notification.table.body.showing')
                    .replace('{{count}}', notifications.length)
                    .replace('{{total}}', paginationInfo.totalDocs)}
            </div>
            <div className="flex items-center gap-4">
                <button 
                    onClick={handlePrevPage} 
                    disabled={!paginationInfo.hasPrevPage || isLoading}
                    className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A306C]"
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {paginationInfo.totalPages}
                </span>
                <button 
                    onClick={handleNextPage} 
                    disabled={!paginationInfo.hasNextPage || isLoading}
                    className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A306C]"
                >
                    Next
                </button>
            </div>
        </div>
      </div>

      {/* MODALS BELOW (NO CHANGES) */}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-0">
          <div
            className="rounded-xl shadow-xl text-white p-4 md:p-6 relative w-full max-w-[480px] max-h-[90vh] overflow-y-auto hide-scrollbar"
            style={{
              background: "radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)",
            }}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-4 text-2xl font-bold"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4">{t('notification.modal.createTitle')}</h2>

            <form className="space-y-4" onSubmit={handleSubmitNotification}>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.titleLabel')}</label>
                <input
                  name="title"
                  value={notificationForm.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  placeholder={t('notification.modal.form.titlePlaceholder')}
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.descLabel')}</label>
                <textarea
                  name="body"
                  value={notificationForm.body}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  placeholder={t('notification.modal.form.descPlaceholder')}
                  rows="3"
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.typeLabel')}</label>
                <select
                  name="type"
                  value={notificationForm.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                >
                  <option value="">{t('notification.modal.form.typeSelect')}</option>
                  {Object.entries(t('notification.modal.form.typeOptions', { returnObjects: true })).map(([key, val]) => (
                    <option key={key} value={key}>{val}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.dateLabel')}</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  placeholder={t('notification.modal.form.datePlaceholder')}
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.driverLabel')}</label>
                <input
                  name="userId"
                  value={notificationForm.userId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  placeholder={t('notification.modal.form.driverPlaceholder')}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sendToAll"
                  checked={notificationForm.total === 'ALL'}
                  onChange={(e) => setNotificationForm(prev => ({
                    ...prev,
                    total: e.target.checked ? 'ALL' : 'SINGLE'
                  }))}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <label htmlFor="sendToAll" className="text-sm">{t('notification.modal.form.sendToAll')}</label>
              </div>
              <button
                type="submit"
                className="mt-6 w-full bg-[#792aff] text-white py-2 rounded-md font-semibold"
              >
                {t('notification.buttons.submit')}
              </button>
            </form>
          </div>
        </div>
      )}

      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-2 md:p-0">
          <div className="bg-[#0F0D2E] p-4 md:p-8 rounded-2xl w-full max-w-md text-center" style={{ backdropFilter: 'blur(42.7px)' }}>
            <h2 style={{ fontFamily: "Poppins" }} className="text-xl md:text-[30px] font-bold text-white leading-tight">
              {t('notification.success.title')}
            </h2>
            <p className="text-xs md:text-sm text-gray-400 mb-4 md:mb-6 mt-1">
              {t('notification.success.message')}
            </p>
            <div className="flex justify-center mb-4 md:mb-6">
              <img src={check} alt="Checkmark" className="w-24 h-20 md:w-[197px] md:h-[156px]" />
            </div>
            <button
              onClick={() => setIsSuccessModalOpen(false)}
              className="w-full py-2 md:py-3 bg-[#6F1AFF] text-white rounded-xl hover:opacity-90 transition font-medium"
            >
              {t('notification.buttons.ok')}
            </button>
          </div>
        </div>
      )}

      {showModal2 && selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-0">
          <div
            className="rounded-xl shadow-xl text-white p-4 md:p-6 relative w-full max-w-[480px] max-h-[90vh] overflow-y-auto hide-scrollbar"
            style={{
              background: "radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)",
            }}
          >
            <button
              onClick={() => setShowModal2(false)}
              className="absolute top-2 right-4 text-2xl font-bold"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4">{t('notification.viewDetails.title')}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.titleLabel')}</label>
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  value={selectedNotification.title || 'N/A'}
                  readOnly
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.viewDetails.description')}</label>
                <textarea
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  value={selectedNotification.body || 'N/A'}
                  readOnly
                  rows="3"
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.modal.form.typeLabel')}</label>
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  value={selectedNotification.type || 'N/A'}
                  readOnly
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.viewDetails.recipient')}</label>
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  value={selectedNotification.userId && selectedNotification.userId._id
                    ? selectedNotification.userId.firstName || t('notification.table.body.specificUser')
                    : t('notification.viewDetails.recipientAll')}
                  readOnly
                />
              </div>
              <div>
                <label className="text-sm block mb-1">{t('notification.viewDetails.createdAt')}</label>
                <input
                  className="w-full px-3 py-2 rounded bg-white text-black"
                  value={selectedNotification.createdAt ? format(new Date(selectedNotification.createdAt), 'PPp') : 'N/A'}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;